import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PAYER_CONFIG } from "../config/payer";
import { PROVIDER_CONFIG } from "../config/provider";
import { mockClaims } from "../data/mock-claims";
import { mockEligibilityInfo } from "../data/mock-eligibility";
import MainLayout from "../layout/main-layout";
import { Claim } from "../types/claim";
import ClaimsTableRow from "./claims-table-row";
import EligibilityCheck from "./eligibility-check";
import EOBModal from "./eob-modal";
import StatusFilter, { StatusValue } from "./status-filter";
import { useAuth } from "../context/auth-context";
import LoginButton from "./login-button";
import { useAuth0 } from "@auth0/auth0-react";

// Add MFAScreen component before DentalPortal
function MFAScreen() {
  const auth = useAuth();
  const [code, setCode] = useState("");

  const handleVerify = async () => {
    const success = await auth.verifyMFA(code);
    if (!success) {
      setCode("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-8">Enter MFA Code</h2>
        {auth.error && (
          <div className="mb-4 p-2 text-red-600 bg-red-50 rounded">
            {auth.error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-1">MFA Code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-2 border rounded"
              placeholder="Enter 6-digit code"
            />
          </div>
          <button
            onClick={handleVerify}
            className="w-full bg-[#42459b] text-white p-2 rounded hover:bg-[#343778]"
          >
            Verify
          </button>
        </div>
      </div>
    </div>
  );
}

interface DentalPortalProps {
  defaultTab?: "claims" | "eligibility";
}

function DentalPortal({ defaultTab }: DentalPortalProps) {
  const auth = useAuth();
  const { isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [patientId, setPatientId] = useState("");
  const [activeTab, setActiveTab] = useState(defaultTab || "claims");
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusValue>("all");
  const [showWelcome, setShowWelcome] = useState(!defaultTab);

  useEffect(() => {
    if (auth.isLoggedIn || isAuthenticated) {
      navigate(`/${activeTab}`, { replace: true });
    }
  }, [activeTab, auth.isLoggedIn, isAuthenticated, navigate]);

  // Handlers
  const handleLogin = async () => {
    const success = await auth.login(username, password);
    if (success) {
      navigate("/claims");
      setUsername("");
      setPassword("");
    } else {
      setPassword("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  const handleTabChange = (tab: "claims" | "eligibility") => {
    setActiveTab(tab);
    setShowWelcome(false);
  };

  // Welcome page component
  const WelcomePage = () => (
    <div className="p-8 text-center">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">
        Welcome to {PAYER_CONFIG.name} Provider Portal
      </h1>
      <h2 className="text-xl text-gray-700 mb-8">{PROVIDER_CONFIG.name}</h2>
      <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
        Your comprehensive solution for managing dental claims and checking
        patient eligibility. Choose an option below to get started.
      </p>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <button
          onClick={() => {
            setShowWelcome(false);
            setActiveTab("claims");
          }}
          className="p-6 text-left bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
        >
          <h2 className="text-xl font-semibold text-blue-600 mb-2">
            Claims Management
          </h2>
          <p className="text-gray-600">
            View and manage dental claims, check status updates, and access EOB
            documents.
          </p>
        </button>
        <button
          onClick={() => {
            setShowWelcome(false);
            setActiveTab("eligibility");
          }}
          className="p-6 text-left bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
        >
          <h2 className="text-xl font-semibold text-blue-600 mb-2">
            Eligibility Verification
          </h2>
          <p className="text-gray-600">
            Check patient eligibility, view coverage details, and verify
            benefits.
          </p>
        </button>
      </div>
    </div>
  );

  // If MFA is required, show MFA screen
  if (auth.requiresMFA) {
    return <MFAScreen />;
  }

  // Render loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading authentication status...</p>
        </div>
      </div>
    );
  }

  // If logged in with either method, show main portal
  if (auth.isLoggedIn || isAuthenticated) {
    return (
      <div className="animate-fadeIn">
        <MainLayout
          username={
            auth.isLoggedIn
              ? auth.username
              : user?.name || user?.email || "User"
          }
          onLogout={auth.isLoggedIn ? auth.logout : undefined}
        >
          {/* EOB Modal */}
          {selectedClaim && (
            <EOBModal
              claim={selectedClaim}
              onClose={() => setSelectedClaim(null)}
            />
          )}

          {showWelcome ? (
            <WelcomePage />
          ) : (
            <>
              {/* Tabs */}
              <div className="border-b flex">
                <button
                  className={`p-3 ${
                    activeTab === "claims" ? "border-b-2 border-blue-500" : ""
                  }`}
                  onClick={() => handleTabChange("claims")}
                >
                  Claims
                </button>
                <button
                  className={`p-3 ${
                    activeTab === "eligibility"
                      ? "border-b-2 border-blue-500"
                      : ""
                  }`}
                  onClick={() => handleTabChange("eligibility")}
                >
                  Eligibility
                </button>
              </div>

              <div className="p-6">
                {activeTab === "claims" ? (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold">Claims List</h2>
                      <StatusFilter
                        value={statusFilter}
                        onChange={setStatusFilter}
                      />
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="p-2 text-left">Claim ID</th>
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-left">Patient</th>
                            <th className="p-2 text-left">Amount</th>
                            <th className="p-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mockClaims
                            .filter((claim) =>
                              statusFilter === "all"
                                ? true
                                : claim.status.code.startsWith(statusFilter)
                            )
                            .map((claim) => (
                              <ClaimsTableRow key={claim.id} claim={claim} />
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <EligibilityCheck
                    patientId={patientId}
                    onPatientIdChange={setPatientId}
                    eligibilityInfo={mockEligibilityInfo}
                  />
                )}
              </div>
            </>
          )}
        </MainLayout>
      </div>
    );
  }

  // If not logged in, show login form
  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
      {/* Tuuthfairy Logo in upper left corner */}
      <div className="absolute top-6 left-6 z-20">
        <svg
          width="137"
          height="37"
          viewBox="0 0 137 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-auto h-9"
        >
          <path
            id="Combined Shape"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.158916 1.04748C5.92913 -1.07528 11.9951 0.0283544 18.357 4.35837C24.7188 8.68838 27.4473 14.9841 26.5425 23.2455C18.7982 22.1301 12.2634 19.7532 6.93802 16.1148C1.61265 12.4765 -0.647048 7.454 0.158916 1.04748ZM8.39756 14.0243C12.7427 16.993 17.9849 19.0773 24.1431 20.2659C24.1501 14.2766 21.757 9.75773 16.9004 6.45222C11.7731 2.96242 7.05366 1.84182 2.57751 2.95958C2.41458 7.63883 4.31496 11.235 8.39756 14.0243ZM26.4912 23.5937C24.4012 23.5937 20.9543 24.4144 16.1503 26.0558C11.3463 27.6972 7.42963 30.3792 4.40038 34.1018C10.0359 36.2271 14.8603 36.5677 18.8734 35.1235C22.8866 33.6793 25.4259 29.836 26.4912 23.5937ZM16.9897 28.4585C13.9625 29.4928 11.3422 30.9707 9.11133 32.9C12.5471 33.7046 15.5008 33.6341 17.996 32.7362C20.2984 31.9076 22.0188 29.9343 23.1163 26.6387C21.4691 27.0231 19.418 27.6288 16.9897 28.4585ZM96.656 23.8264H94.3064V31.6326H91.5919V23.8264H90.0966V22.5357L91.5919 21.8156V21.0956C91.5919 19.9775 91.8707 19.1609 92.4285 18.6458C92.9862 18.1306 93.8792 17.873 95.1074 17.873C96.0449 17.873 96.8785 18.0106 97.6083 18.2857L96.9141 20.2526C96.3683 20.0829 95.8639 19.998 95.4011 19.998C95.0154 19.998 94.7366 20.1107 94.5645 20.3361C94.3924 20.5614 94.3064 20.8497 94.3064 21.201V21.8156H96.656V23.8264ZM84.5924 31.6325H87.3348V25.2372C87.3348 24.0792 87.0291 23.1903 86.4177 22.5703C85.8062 21.9504 84.9101 21.6404 83.7292 21.6404C82.3985 21.6404 81.4274 22.12 80.816 23.0792H80.6721L80.7351 22.2896C80.777 21.4884 80.798 20.9796 80.798 20.7632V17.9822H78.0556V31.6325H80.798V27.0181C80.798 25.8776 80.9689 25.053 81.3105 24.5442C81.6522 24.0354 82.2067 23.781 82.9739 23.781C84.0529 23.781 84.5924 24.4886 84.5924 25.904V31.6325ZM38.1961 29.602C38.682 29.602 39.2651 29.4992 39.9453 29.2936V31.2933C39.2529 31.5929 38.4026 31.7426 37.3944 31.7426C36.2829 31.7426 35.4735 31.471 34.9664 30.9278C34.4592 30.3845 34.2056 29.5697 34.2056 28.4832V23.735H32.8755V22.5986L34.4061 21.7001L35.2078 19.6211H36.9844V21.7177H39.836V23.735H36.9844V28.4832C36.9844 28.8649 37.0952 29.1468 37.3169 29.3289C37.5386 29.5109 37.8317 29.602 38.1961 29.602ZM49.9926 31.5675L49.6239 30.3188H49.48C49.1863 30.7729 48.7697 31.1236 48.2302 31.371C47.6907 31.6184 47.0763 31.7421 46.387 31.7421C45.2061 31.7421 44.316 31.4351 43.7165 30.8209C43.1171 30.2067 42.8174 29.3233 42.8174 28.1706V21.8047H45.5598V27.5069C45.5598 28.2114 45.6886 28.7397 45.9464 29.0919C46.2042 29.4441 46.6148 29.6202 47.1782 29.6202C47.9455 29.6202 48.5 29.3713 48.8416 28.8736C49.1833 28.3758 49.3542 27.5506 49.3542 26.3979V21.8047H52.0965V31.5675H49.9926ZM62.5482 30.3188L62.9169 31.5675H65.0209V21.8047H62.2785V26.3979C62.2785 27.5506 62.1076 28.3758 61.766 28.8736C61.4243 29.3713 60.8698 29.6202 60.1025 29.6202C59.5391 29.6202 59.1285 29.4441 58.8707 29.0919C58.613 28.7397 58.4841 28.2114 58.4841 27.5069V21.8047H55.7417V28.1706C55.7417 29.3233 56.0414 30.2067 56.6408 30.8209C57.2403 31.4351 58.1304 31.7421 59.3113 31.7421C60.0006 31.7421 60.6151 31.6184 61.1545 31.371C61.694 31.1236 62.1106 30.7729 62.4044 30.3188H62.5482ZM73.3245 29.602C73.8104 29.602 74.3935 29.4992 75.0737 29.2936V31.2933C74.3813 31.5929 73.531 31.7426 72.5228 31.7426C71.4113 31.7426 70.602 31.471 70.0948 30.9278C69.5876 30.3845 69.3341 29.5697 69.3341 28.4832V23.735H68.0039V22.5986L69.5345 21.7001L70.3362 19.6211H72.1128V21.7177H74.9644V23.735H72.1128V28.4832C72.1128 28.8649 72.2236 29.1468 72.4453 29.3289C72.667 29.5109 72.9601 29.602 73.3245 29.602ZM105.11 30.3302L105.639 31.6741H107.551V25.0872C107.551 23.9083 107.192 23.03 106.474 22.4524C105.756 21.8747 104.724 21.5859 103.378 21.5859C101.971 21.5859 100.691 21.8836 99.5364 22.4789L100.443 24.3003C101.526 23.8228 102.468 23.5841 103.27 23.5841C104.311 23.5841 104.831 24.0851 104.831 25.0872V25.5204L103.09 25.5735C101.589 25.6265 100.465 25.9021 99.7203 26.4001C98.9754 26.8982 98.603 27.6719 98.603 28.7211C98.603 29.7231 98.8797 30.4953 99.4332 31.0375C99.9866 31.5798 100.745 31.851 101.708 31.851C102.492 31.851 103.128 31.7405 103.615 31.5194C104.103 31.2984 104.577 30.902 105.038 30.3302H105.11ZM104.679 26.9375L103.618 26.9736C102.821 26.9976 102.228 27.1418 101.839 27.4062C101.449 27.6706 101.254 28.0732 101.254 28.614C101.254 29.3892 101.698 29.7768 102.585 29.7768C103.22 29.7768 103.728 29.5935 104.108 29.227C104.489 28.8604 104.679 28.3737 104.679 27.7668V26.9375ZM112.688 17.9822C111.693 17.9822 111.196 18.4357 111.196 19.3427C111.196 20.2557 111.693 20.7123 112.688 20.7123C113.185 20.7123 113.557 20.5921 113.806 20.3516C114.055 20.1112 114.179 19.7749 114.179 19.3427C114.179 18.4357 113.682 17.9822 112.688 17.9822ZM114.178 31.6329H111.306V21.8047H114.178V31.6329ZM124.562 21.7736C124.317 21.7213 124.009 21.6951 123.639 21.6951C123.017 21.6951 122.442 21.8623 121.913 22.1968C121.384 22.5312 120.964 22.9718 120.653 23.5186H120.519L120.115 21.8783H118.044V31.6326H120.779V26.6682C120.779 25.883 121.022 25.2723 121.509 24.836C121.996 24.3998 122.676 24.1817 123.549 24.1817C123.866 24.1817 124.135 24.2107 124.356 24.2689L124.562 21.7736ZM125.889 21.9141H128.897L130.798 27.4177C130.96 27.896 131.071 28.4618 131.131 29.1151H131.185C131.251 28.5143 131.381 27.9485 131.573 27.4177L133.437 21.9141H136.383L132.122 32.9476C131.732 33.9684 131.175 34.7325 130.451 35.24C129.728 35.7475 128.882 36.0012 127.915 36.0012C127.441 36.0012 126.976 35.9516 126.519 35.8525V33.735C126.85 33.8109 127.21 33.8488 127.6 33.8488C128.087 33.8488 128.512 33.7044 128.875 33.4157C129.238 33.1269 129.522 32.6909 129.726 32.1076L129.888 31.6263L125.889 21.9141Z"
            fill="#42459b"
          ></path>
        </svg>
      </div>

      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg animate-fadeIn z-10">
        <h2 className="text-2xl font-bold mb-8 text-slate-700">
          {PAYER_CONFIG.name} Provider Portal
        </h2>
        {auth.error && (
          <div className="mb-4 p-2 text-red-600 bg-red-50 rounded">
            {auth.error}
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-1">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-2 border rounded"
              placeholder="Enter username"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-2 border rounded"
              placeholder="Enter password"
            />
          </div>
          <button
            onClick={handleLogin}
            className="w-full bg-[#42459b] text-white p-2 rounded hover:bg-[#343778]"
          >
            Login
          </button>

          <div className="text-center">
            <p className="flex items-center justify-center text-sm text-gray-600 mb-2">
              <span className="border-t border-gray-300 flex-grow mr-3"></span>
              Or
              <span className="border-t border-gray-300 flex-grow ml-3"></span>
            </p>
            <LoginButton />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DentalPortal;
